import { Flex, Text, ThemeUICSSProperties, ThemeUIStyleObject } from 'theme-ui';

import ImgLoader from '~/components/Generic/ImgLoader';
import { OptionalImageIcon } from './sharedTypes';
import { getHoverEffectStyle, getIconComponent } from './utils';
import LinkRenderer from '~/components/Generic/LinkRenderer';
import { TextX, TextY, ResponsiveSize } from '~/types/style';
import { textToAlignValue, getDisplayProperty, getResponsiveSize } from '~/utils/style-helpers';

type Mode = 'responsive' | 'manual';
type RichImageVariantImage = 'primary' | 'secondary';

export const RichImageComponent = ({
  richImage,
  hover,
  textY = 'center',
  textX = 'center',
  textYMobile,
  textXMobile,
  optionalImageIcon,
  locationDetail = 'RichImageComponent',
  mode = 'responsive',
  variantImage = ['secondary', 'primary', 'primary'],
  forwardSx = {},
}: {
  forwardSx?: ThemeUIStyleObject;
  richImage: Queries.RichImageFragmentFragment;
  hover: string;
  textY: TextY;
  textX: TextX;
  textYMobile?: TextY;
  textXMobile?: TextX;
  optionalImageIcon?: OptionalImageIcon;
  locationDetail?: string;
  variantImage?: RichImageVariantImage | [RichImageVariantImage, RichImageVariantImage, RichImageVariantImage];
  /**
   * Controls how the primary and secondary images are used.
   * If `responsive`, the primary image is used on desktop
   * and the secondary image is used on mobile.
   *
   * If `manual`, the primary and secondary
   * images are rendered according to the variantImage prop.
   */
  mode?: Mode;
}) => {
  const IconComponent = getIconComponent(optionalImageIcon ?? 'none');
  const hoverEffectStyle = getHoverEffectStyle(hover);

  const Action = richImage?.cta;

  const sharedImageStyle: ThemeUIStyleObject = {
    objectFit: 'cover',
    height: '100%',
    width: '100%',
    position: 'absolute',
  };

  /**
   * - First get the correct image for variant prop.
   * - Then, set the responsive display rules for the image based
   *   on the size prop.
   */
  const getImageComponent = (variant: RichImageVariantImage, size: ResponsiveSize) => {
    // Fallback to primary image if secondary image is not defined.
    const secondaryImage = richImage?.secondaryImage ?? richImage.primaryImage;

    let image = null;
    switch (variant) {
      case 'secondary':
        image = secondaryImage;
        break;
      case 'primary':
      default:
        image = richImage.primaryImage;
    }

    const display = getDisplayProperty(size);

    return (
      <ImgLoader
        data-testid={`image-${variant}-${size}-size`}
        forwardSx={{
          display: display,
          ...sharedImageStyle,
        }}
        image={image}
      />
    );
  };

  // ['primary'] === all primary. ['primary', 'secondary'] === primary on mobile, secondary on desktop + tablet.

  /**
   * Render images according to variantImage prop.
   * ['primary', 'secondary'] renders primary image
   * on mobile and secondary image on desktop.
   * ['secondary'] renders secondary image on all sizes.
   * 'primary' renders primary image on all sizes.
   */
  const renderManualImage = () => {
    if (Array.isArray(variantImage)) {
      // If variantImage is an array, render images according to the responsive rules.
      return (
        <>
          {variantImage.map((variant, index, array) => {
            const responsiveSize = getResponsiveSize(index, array.length);

            return getImageComponent(variant, responsiveSize);
          })}
        </>
      );
    } else {
      return getImageComponent(variantImage, 'all');
    }
  };

  const ResponsiveImageComponent = (
    <>
      {getImageComponent('primary', 'desktop')}
      {getImageComponent('primary', 'tablet')}
      {getImageComponent('secondary', 'mobile')}
    </>
  );

  const ImageComponent = mode === 'responsive' ? ResponsiveImageComponent : renderManualImage();

  const actionStyle: ThemeUIStyleObject = !!Action
    ? {
        ...hoverEffectStyle,
        cursor: 'pointer',
        '.icon-component': {
          opacity: 0,
          transition: 'var(--transition)',
        },
        ':hover .icon-component': {
          opacity: 1,
        },
      }
    : {};

  const textYStyle = [
    textToAlignValue(textYMobile ?? textY),
    textToAlignValue(textY),
  ] as ThemeUICSSProperties['alignItems'];
  const textXStyle = [
    textToAlignValue(textXMobile ?? textX),
    textToAlignValue(textX),
  ] as ThemeUICSSProperties['justifyContent'];

  const RichImageComponent = (
    <Flex
      data-testid="rich-image-component"
      sx={{
        '--transition': '0.3s ease-in-out',
        transition: 'var(--transition)',
        height: '100%',
        width: '100%',
        position: 'relative',
        isolation: 'isolate',
        color: richImage?.color ?? 'black',
        ...actionStyle,
        ...forwardSx,
      }}
    >
      <Flex
        data-testid="rich-image-component-content"
        sx={{
          alignItems: textYStyle,
          justifyContent: textXStyle,
          height: '100%',
          width: '100%',
        }}
      >
        {!!richImage?.imageTitle && (
          <Text
            variant="h6"
            sx={{
              position: 'relative',
              zIndex: 2,
              fontSize: ['1.8rem', '2rem'],
              p: '3md',
            }}
          >
            {richImage.imageTitle}
          </Text>
        )}
        {!!IconComponent && !!Action && (
          <div
            data-testid="hover-icon"
            className="icon-component"
            sx={{ position: 'absolute', zIndex: 2, bottom: '3md', right: '3md' }}
          >
            {IconComponent}
          </div>
        )}
        {ImageComponent}
      </Flex>
    </Flex>
  );

  if (Action)
    return (
      //@ts-ignore
      <LinkRenderer
        sx={{ width: '100%', height: '100%', ...forwardSx }}
        page={Action?.page ?? {}}
        slug={Action?.page?.slug}
        title={Action?.page?.title}
        href={Action?.url}
        innerLink={Action?.innerLink}
        type={Action?.page?.__typename}
        locationDetail={locationDetail}
      >
        {RichImageComponent}
      </LinkRenderer>
    );

  return RichImageComponent;
};
